import React, { useCallback } from "react";
import { useAppContext } from "../../../AppProvider.jsx";
import { useTypes } from "../../../utilities/types.js";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal, ModalBody, UncontrolledTooltip } from "reactstrap";
import moment from "moment";
import { TrackingProvider } from "../providers/Tracking.tsx";
import Tracking from "../Tracking.tsx";
import ButtonUtility from "../../../Components/Buttons/Utility.tsx";
import { Shipment } from "../../../Models/Shipment.ts";
import InputBoxIcon from "../../../Components/Inputs/InputIcon/InputIcon.tsx";
import {
  faCoins,
  faUser,
  faEnvelope,
  faClone,
  faCalendarPlus,
  faCalendar,
  faAngleLeft,
  faPenToSquare,
  faCube,
  faWeightHanging,
  faShield,
  faClock,
  faLongArrowRight,
  faWallet,
  faLoader,
  faImageSlash,
  faBagsShopping,
  faExchangeAlt,
  faInfoCircle,
  faLockOpenLight,
} from "../../../utilities/fontawesomeIcons.js";
import Rating from "../../../Components/Rating/Rating.tsx";
import { Handler as HandlerFeedback } from "../../Feedback/Handler.tsx";
import { HandlerProvider as HandlerFeedbackProvider } from "../../Feedback/providers/Handler.tsx";
import { CustomDataIndex } from "../../CustomDatas/CustomDataIndex.tsx";
import { AttachmentIndex } from "../../Attachments/AttachmentsIndex.tsx";
import { AttachmentIndexProvider } from "../../Attachments/providers/AttachmentIndexProvider.tsx";
import { ParcelsIndex } from "../../Parcels/ParcelIndex.tsx";
import { IndexEmailNotificationsProvider } from "../../EmailNotifications/providers/Index.tsx";
import EmailNotifications from "../../EmailNotifications/index.tsx";
import ButtonConfirm from "../../../Components/Buttons/Confirm.tsx";
import ButtonDelete from "../../../Components/Buttons/Delete.tsx";
import { HandlerStockReleaseShipmentProvider } from "../../StockReleaseShipments/providers/Handler.tsx";
import { Handler as HandlerStockReleaseShipment } from "../../StockReleaseShipments/Handler.tsx";
import { ShipmentCostsIndex } from "../../ShipmentCosts/ShipmentCostsIndex.tsx";
import { TooltipCopy } from "../../../Components/common/Tooltips/TooltipCopy.jsx";
import { ShipmentValues } from "./Values.tsx";
import { DetailsCostsWeight } from "./Weight.tsx";
import { ShipmentDeleteProvider } from "../providers/ShipmentDeleteProvider.tsx";
import ShipmentDelete from "../ShipmentDelete.tsx";
import { ShipmentConfirmProvider } from "../providers/ShipmentConfirmProvider.tsx";
import ShipmentConfirm from "../ShipmentConfirm.tsx";
import { CollectionHandlerProvider } from "../../Collections/providers/CollectionHandlerProvider.tsx";
import { CollectionHandler } from "../../Collections/CollectionHandler.tsx";
import { useShipmentDetailsContext } from "../providers/hooks.ts";
import { responsiveDetails } from "../Layout/Details/ResponsiveDetails.ts";
import { AddressBookHandlerProvider } from "../../AddressBook/providers/AddressBookHandlerProvider.tsx";
import { AddressBookHandler } from "../../AddressBook/AddressBookHandler.tsx";

export const Details: React.FC = () => {
  const { id } = useParams();
  const context = useShipmentDetailsContext();
  const { data, loader, setAcr, acr, getShipmentDetails } = context;
  const modalConfig = context?.modalConfig;
  const setModalConfig = context?.setModalConfig;
  const { dictionary, display } = useAppContext();
  const { shipments, shipmentStatus, parcels, currencies } = useTypes();
  const navigate = useNavigate();

  const styles = responsiveDetails(display);

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = "",
    ) => {
      context?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [context],
  );

  const closeModal = () => {
    if (setModalConfig) {
      setModalConfig(createModalConfig());
    }
  };

  const icons = {
    shipment: shipments.find((o) => o.value === data.shipmentType),
    parcel: parcels.find((p) => p.value === data.parcelType?.code),
  };
  const status = {
    shipmentStatus: shipmentStatus.find((o) => o.value === data.status?.code),
  };
  const valueCurrency = {
    cashOnDeliveryValueCurrency: currencies.find(
      (c) => c.value === data.cashOnDeliveryValueCurrency,
    ),
    insuranceValueCurrency: currencies.find(
      (c) => c.value === data.insuranceValueCurrency,
    ),
    goodsValueCurrency: currencies.find(
      (c) => c.value === data.goodsValueCurrency,
    ),
  };

  const selectedCurrency =
    valueCurrency.cashOnDeliveryValueCurrency ||
    valueCurrency.insuranceValueCurrency ||
    valueCurrency.goodsValueCurrency;

  const pickupName =
    data.pickup?.contactName === data.pickup?.name
      ? (data.pickup?.contactName ?? "")
      : [
          data.pickup?.contactName,
          data.pickup?.name ? `c/o ${data.pickup.name}` : null,
        ]
          .filter(Boolean)
          .join(" ");

  const deliveryName =
    data.delivery?.contactName === data.delivery?.name
      ? (data.delivery?.contactName ?? "")
      : [
          data.delivery?.contactName,
          data.delivery?.name ? `c/o ${data.delivery.name}` : null,
        ]
          .filter(Boolean)
          .join(" ");

  return (
    <>
      {/* ---------------------------------------------------------- */}
      {/*                        MODALS                              */}
      {/* ---------------------------------------------------------- */}
      <Modal isOpen={modalConfig?.isOpen} size={modalConfig?.size}>
        <ModalBody className={modalConfig?.className || ""}>
          <h3 className="text-center mb-2">{modalConfig.title}</h3>
          {modalConfig?.action === "shipmentCost" && (
            <>
              <div className="d-flex align-items-center flex-column gap-3">
                <div className="w-100 mb-3">
                  <ShipmentCostsIndex />
                </div>
              </div>
              <ButtonUtility
                className="w-100"
                onClick={() => closeModal()}
                disabled={loader}
              >
                {dictionary.actions.close}
              </ButtonUtility>
            </>
          )}
          {modalConfig?.action === "feedback" && (
            <>
              {data.feedback?.id && (
                <HandlerFeedbackProvider
                  id={data.feedback.id}
                  callback={closeModal}
                >
                  <HandlerFeedback />
                </HandlerFeedbackProvider>
              )}
            </>
          )}
          {(modalConfig?.action === "pickup" ||
            modalConfig?.action === "delivery") && (
            <>
              <AddressBookHandlerProvider
                id={
                  modalConfig?.action === "pickup"
                    ? data.pickup?.id
                    : data.delivery?.id
                }
                action="details"
                service="shipment"
              >
                <AddressBookHandler />
              </AddressBookHandlerProvider>
              <ButtonUtility
                className="w-100 mt-3"
                onClick={() => closeModal()}
                disabled={loader}
              >
                {dictionary.actions.close}
              </ButtonUtility>
            </>
          )}
          {modalConfig?.action === "weightAndVolume" && (
            <>
              <DetailsCostsWeight />
              <div className="d-flex flex-column w-100 gap-1">
                <ButtonUtility
                  className="w-100"
                  onClick={() => closeModal()}
                  disabled={loader}
                >
                  {dictionary.actions.close}
                </ButtonUtility>
              </div>
            </>
          )}

          {modalConfig?.action === "shipmentValues" && (
            <>
              <ShipmentValues />
              <div className="d-flex flex-column w-100 gap-1">
                <ButtonUtility
                  className="w-100"
                  onClick={() => closeModal()}
                  disabled={loader}
                >
                  {dictionary.actions.close}
                </ButtonUtility>
              </div>
            </>
          )}

          {/* MODALS OF THE FOOTER BUTTON */}

          {modalConfig.action == "createStockRelease" && (
            <HandlerStockReleaseShipmentProvider
              event={{
                senderId: data.senderId as string,
                shipmentId: data.id as string,
                deliveryId: data.delivery?.id,
                carrierCode: data.contractService?.carrierService.carrier.code,
                internalReference: data.internalReference as string,
                carrierReference: data.carrierReference as string,
                note: data.statusEventNote,
                utc: true,
                stock: true,
                eventDate: new Date().toISOString(),
              }}
              callback={() => {
                id && getShipmentDetails(id);
                closeModal();
              }}
              action="create"
            >
              <HandlerStockReleaseShipment />
            </HandlerStockReleaseShipmentProvider>
          )}

          {modalConfig.action === "emailNotifications" && (
            <>
              <IndexEmailNotificationsProvider
                parentId={modalConfig.data?.[0]}
                action="details"
                callback={() => {
                  getShipmentDetails(id as string);
                  closeModal();
                }}
              >
                <EmailNotifications />
              </IndexEmailNotificationsProvider>
              <ButtonUtility
                className="mt-2"
                onClick={() => {
                  id && getShipmentDetails(id);
                  closeModal();
                }}
              >
                {dictionary.actions.cancel}
              </ButtonUtility>
            </>
          )}

          {modalConfig.action === "delete" && (
            <ShipmentDeleteProvider
              data={modalConfig?.data}
              callback={() => {
                closeModal();
              }}
            >
              <ShipmentDelete />
            </ShipmentDeleteProvider>
          )}

          {modalConfig.action == "confirm" && (
            <ShipmentConfirmProvider
              data={modalConfig?.data}
              callback={() => {
                closeModal();
              }}
            >
              <ShipmentConfirm />
            </ShipmentConfirmProvider>
          )}

          {modalConfig.action == "createCollection" && (
            <>
              <CollectionHandlerProvider
                title=""
                autosave={false}
                callback={() => {
                  setAcr(false);
                  id && getShipmentDetails(id);
                  closeModal();
                }}
                action="create"
                carrierCode={data.contractService?.contract?.carrierCode}
                defaultAddressBookId={data.pickup?.id}
                shipmentId={data.id}
              >
                <CollectionHandler />
              </CollectionHandlerProvider>
            </>
          )}
        </ModalBody>
      </Modal>

      {/* ---------------------------------------------------------- */}
      {/*                           PAGE                             */}
      {/* ---------------------------------------------------------- */}
      {loader ? (
        <>
          <div className="d-flex align-items-center justify-content-center h-100">
            <FontAwesomeIcon icon={faLoader} spin size="3x" />
          </div>
        </>
      ) : (
        <>
          {/* -------------------------------------------------------- */}
          {/*               INTESTAZIONE AND FEEDBACK                  */}
          {/* -------------------------------------------------------- */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center align-items-md-center mb-3 gap-3">
            {/* Titolo + numero spedizione */}
            <div className="d-flex flex-column flex-md-row align-items-center align-items-md-center gap-2 w-100 w-md-50">
              <h2 className="m-0 d-flex align-items-center gap-2 flex-wrap ">
                {dictionary.shipments.shipment}

                <TooltipCopy
                  placement="right"
                  target={"carrierReference"}
                  textToCopy={data.carrierReference}
                >
                  <span>
                    #
                    <span
                      id="carrierReference"
                      className="fw-bold cursor-pointer"
                    >
                      {data.carrierReference}
                    </span>
                  </span>
                </TooltipCopy>
              </h2>
            </div>

            {/* Rating + Status */}
            <div className="d-flex flex-column flex-md-row align-items-center align-items-md-center justify-content-end gap-2 w-100 w-md-50">
              {/* Rating */}
              <div className="mb-2 mb-md-0 d-flex justify-content-center justify-content-md-end w-100 w-md-75">
                {data.feedback?.rating ? (
                  <div
                    title={`${dictionary.feedbacks.feedback}: ${data.feedback.rating}/5 `}
                    onClick={() => {
                      if (id) {
                        openModal("feedback", [id], "md", "", "");
                      }
                    }}
                    className="cursor-pointer mt-1"
                  >
                    <Rating value={data.feedback.rating} hidden={true} />
                  </div>
                ) : (
                  <div title={`${dictionary.feedbacks.no_feedback_present} `}>
                    <Rating value={0} hidden={true} />
                  </div>
                )}
              </div>

              {/* Status */}
              <div
                className={`position-relative status py-2 d-flex align-items-center justify-content-center w-100 w-md-50
                  ${
                    data.pickup?.name
                      ? "box-container hover-card-btn cursor-pointer"
                      : "box-container-noshadow"
                  }`}
                style={{
                  backgroundColor: status.shipmentStatus?.color,
                }}
              >
                <div className="d-flex align-items-center gap-1 me-3">
                  {status.shipmentStatus?.label}
                  {data.status?.code === "DELIVERED" && (
                    <>
                      <FontAwesomeIcon
                        id={"statusEventDate"}
                        className="cursor-help"
                        style={{ zIndex: 100 }}
                        icon={faInfoCircle}
                      />

                      <UncontrolledTooltip
                        placement="top"
                        target={"statusEventDate"}
                      >
                        {moment(data.statusEventDate).format(
                          "DD/MM/YYYY HH:mm",
                        )}
                      </UncontrolledTooltip>
                    </>
                  )}
                </div>
                <div>
                  <FontAwesomeIcon
                    icon={faLongArrowRight}
                    color={"#fff"}
                    size="1x"
                  />
                </div>

                {id && (
                  <TrackingProvider shipment={data as Shipment}>
                    <Tracking />
                  </TrackingProvider>
                )}
              </div>
            </div>
          </div>

          {/* -------------------------------------------------------- */}
          {/*                           MAIN                           */}
          {/* -------------------------------------------------------- */}
          <div className="d-grid gap-2" style={styles.main}>
            {/* -------------------------------------------------------- */}
            {/*                         HEADER                           */}
            {/* -------------------------------------------------------- */}
            <div className="d-grid gap-2" style={styles.header}>
              {/* -------------------------------------------------------- */}
              {/*              CONTAINER CARD INFO COSTS                   */}
              {/* -------------------------------------------------------- */}
              <div
                className="d-grid w-100 box-container-noshadow align-items-center "
                style={styles.cardInformationCost}
              >
                {/* CARRIER */}
                <div
                  className="d-flex align-items-center justify-content-center rounded-2 h-100 p-1 px-2 py-4  me-2"
                  style={{
                    ...styles.cells.carrier,
                    lineHeight: "1",

                    backgroundColor:
                      data.contractService?.contract.carrierCode ===
                      "POSTEBUSINESS"
                        ? "#ecdc00"
                        : "#f8f8f8",
                  }}
                >
                  {data.contractService?.name ? (
                    <img
                      src={
                        data.contractService?.contract.carrierConfiguration
                          ?.carrierLogo
                      }
                      style={{
                        width: "90px",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faImageSlash}
                        color="#bbb"
                        size="2x"
                      />
                    </>
                  )}
                </div>
                <div
                  style={styles.cells.nameCarrierAndShipmentType}
                  className="d-flex justify-content-center flex-column h-100 w-100 gap-2"
                >
                  {data.contractService?.carrierService.name ? (
                    <label
                      className="label-8"
                      style={{
                        lineHeight: "1",
                      }}
                    >
                      {data.contractService.carrierService.name}
                    </label>
                  ) : (
                    <div className="label-8">
                      {dictionary.services.no_service_related}
                    </div>
                  )}
                  {/* SHIPMENT TYPE */}
                  <div
                    className={`d-flex align-items-center justify-content gap-1 w-auto ${icons.shipment ? "" : "text-disabled"}`}
                  >
                    {icons.shipment ? (
                      <>
                        <FontAwesomeIcon
                          icon={icons?.shipment?.icon}
                          title={`${dictionary.shipments.shipment}: ${icons.shipment?.label}`}
                          color={icons.shipment?.color}
                          flip={
                            data.shipmentType === "EXPRESS_RETURN"
                              ? "horizontal"
                              : undefined
                          }
                        />
                        <label
                          className="label-6 m-0 "
                          style={{
                            color: icons.shipment?.color,
                            lineHeight: "1",
                          }}
                        >
                          {dictionary.shipments.shipment}{" "}
                          {icons.shipment?.label}
                        </label>
                      </>
                    ) : (
                      <>-</>
                    )}
                  </div>
                </div>
              </div>

              {/* -------------------------------------------------------- */}
              {/*            CONTAINER CARD INFORMATION                    */}
              {/* -------------------------------------------------------- */}
              <div
                style={styles.cells.costShipment}
                className={`d-flex flex-column justify-content-between  ${
                  data.ratingCost
                    ? "box-container hover-card-btn cursor-pointer"
                    : "box-container-noshadow"
                }`}
                onClick={() => {
                  if (id && (data.ratingCost || data.ratingTime)) {
                    openModal(
                      "shipmentCost",
                      [id],
                      "sm",
                      "",
                      `${dictionary.words.costs} ${dictionary.words.and} ${dictionary.dates.delivery_time}`,
                    );
                  }
                }}
              >
                <div>
                  <div
                    className={`d-flex align-items-center gap-3 mb-2 ${data.ratingCost ? "" : "text-disabled"}`}
                  >
                    <FontAwesomeIcon
                      icon={faCoins}
                      id="rating-cost"
                      className="icon cursor-help"
                    />
                    <UncontrolledTooltip placement="top" target="rating-cost">
                      {dictionary.words.costs}
                    </UncontrolledTooltip>
                    <label
                      className={`label-1 ${
                        data.ratingCost ? "cursor-pointer" : ""
                      }`}
                    >
                      {data.ratingCost ? (
                        <>{`${data.ratingCost?.toFixed(2)} ${selectedCurrency?.label || ""}`}</>
                      ) : (
                        "-"
                      )}
                    </label>
                  </div>
                  <div
                    className={`d-flex align-items-center gap-3 ${data.ratingTime ? "" : "text-disabled"}`}
                  >
                    <FontAwesomeIcon
                      icon={faClock}
                      id="rating-time"
                      className="icon cursor-help"
                    />
                    {data.ratingTime !== 0 && (
                      <UncontrolledTooltip
                        placement="bottom"
                        target="rating-time"
                      >
                        {dictionary.dates.delivery_time}
                      </UncontrolledTooltip>
                    )}
                    <label
                      className={`label-1 ${
                        data.ratingTime ? "cursor-pointer" : ""
                      }`}
                    >
                      {data.ratingTime ? <>{data.ratingTime} h</> : "-"}
                    </label>
                  </div>
                </div>

                <div className="text-end mt-2">
                  {data.ratingCost || data.ratingTime ? (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#191919"}
                        size="1x"
                      />
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#19191900"}
                        size="1x"
                      />
                    </>
                  )}
                </div>
              </div>

              <div
                style={styles.cells.weightAndVolume}
                className={`d-flex flex-column justify-content-between ${
                  data.weight || data.volume
                    ? " box-container hover-card-btn cursor-pointer"
                    : "box-container-noshadow"
                }`}
                onClick={() => {
                  if (id && (data.weight || data.volume)) {
                    openModal(
                      "weightAndVolume",
                      [id],
                      "sm",
                      "",
                      `${dictionary.actions.details} ${dictionary.dimensions.weight}`,
                    );
                  }
                }}
              >
                <div>
                  <div
                    className={`d-flex align-items-center gap-3 mb-2 ${data.weight ? "" : "text-disabled"}`}
                  >
                    <FontAwesomeIcon
                      id="weight"
                      icon={faWeightHanging}
                      className="icon cursor-help"
                    />
                    <UncontrolledTooltip placement="top" target="weight">
                      {dictionary.dimensions.weight}
                    </UncontrolledTooltip>
                    <label
                      className={`label-1 ${
                        data.weight ? "cursor-pointer" : ""
                      }`}
                    >
                      {data.weight ? <>{data.weight?.toFixed(2)} Kg</> : "-"}
                    </label>
                  </div>
                  <div
                    className={`d-flex align-items-center gap-3 ${data.volume ? "" : "text-disabled"}`}
                  >
                    <FontAwesomeIcon
                      icon={faCube}
                      id="volume"
                      className="icon cursor-help"
                    />
                    <UncontrolledTooltip placement="bottom" target="volume">
                      {dictionary.dimensions.volume}
                    </UncontrolledTooltip>
                    <label
                      className={`label-1 ${
                        data.volume ? "cursor-pointer" : ""
                      }`}
                    >
                      {data.volume ? (
                        <>
                          {data.volume.toFixed(6)} m<sup>3</sup>
                        </>
                      ) : (
                        "-"
                      )}
                    </label>
                  </div>
                </div>

                <div className="text-end mt-2">
                  {data.weight || data.volume ? (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#191919"}
                        size="1x"
                      />
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#19191900"}
                        size="1x"
                      />
                    </>
                  )}
                </div>
              </div>

              <div
                style={styles.cells.valueShipmentCost}
                className={` d-flex flex-column justify-content-between  ${
                  data.insuranceValue || data.cashOnDeliveryValue
                    ? " box-container hover-card-btn cursor-pointer "
                    : " box-container-noshadow "
                }`}
                onClick={() => {
                  if (id && (data.insuranceValue || data.cashOnDeliveryValue)) {
                    openModal(
                      "shipmentValues",
                      [id],
                      "sm",
                      "",
                      `Valori spedizione`,
                    );
                  }
                }}
              >
                <div>
                  <div
                    className={`d-flex align-items-center gap-3 mb-2 ${data.insuranceValue ? "" : "text-disabled"}`}
                  >
                    <FontAwesomeIcon
                      icon={faShield}
                      id="insurance-value"
                      className="icon cursor-help"
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target="insurance-value"
                    >
                      {dictionary.dimensions.insurance_value}
                    </UncontrolledTooltip>
                    <label
                      className={`label-1 ${
                        data.insuranceValue ? "cursor-pointer" : ""
                      }`}
                    >
                      {data.insuranceValue
                        ? `${data.insuranceValue.toFixed(2)} ${valueCurrency.insuranceValueCurrency?.label}`
                        : "-"}
                    </label>
                  </div>
                  <div
                    className={`d-flex align-items-center gap-3 ${data.cashOnDeliveryValue ? "" : "text-disabled"}`}
                  >
                    <FontAwesomeIcon
                      icon={faWallet}
                      id="cashOnDeliveryValue"
                      className="icon cursor-help"
                      size="lg"
                    />
                    <UncontrolledTooltip
                      placement="bottom"
                      target="cashOnDeliveryValue"
                    >
                      {dictionary.dimensions.cash_on_delivery_value}
                    </UncontrolledTooltip>
                    <label
                      className={`label-1 ${
                        data.cashOnDeliveryValue ? "cursor-pointer" : ""
                      }`}
                    >
                      {data.cashOnDeliveryValue
                        ? `${data.cashOnDeliveryValue.toFixed(2)} ${valueCurrency.cashOnDeliveryValueCurrency?.label}`
                        : "-"}
                    </label>
                  </div>
                </div>

                <div className="text-end mt-2">
                  {data.insuranceValue || data.cashOnDeliveryValue ? (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#191919"}
                        size="1x"
                      />
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#19191900"}
                        size="1x"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div
              style={styles.cells.pickupAndDelivery}
              className="d-flex gap-2 "
            >
              <div
                className={` d-flex flex-column justify-content-between ${
                  data.pickup
                    ? " box-container hover-card-btn cursor-pointer "
                    : " box-container-noshadow "
                } `}
                onClick={() => {
                  if (id && data.pickup) {
                    openModal(
                      "pickup",
                      [id],
                      "md",
                      "",
                      dictionary.registries.sender,
                    );
                  }
                }}
              >
                <div
                  className={`${data.pickup && data.pickup.location?.label ? "" : "text-disabled"}`}
                >
                  <h3 className={`${data.pickup ? "cursor-pointer" : ""}`}>
                    {dictionary.registries.sender}
                  </h3>
                  {data.pickup && data.pickup.location?.label ? (
                    <>
                      <label
                        className={`label-1 mb-1 ${
                          data.pickup ? "cursor-pointer" : ""
                        }`}
                      >
                        {pickupName}
                      </label>
                      <label
                        className={`label-6 ${
                          data.pickup ? "cursor-pointer" : ""
                        }`}
                      >
                        {data.pickup?.location?.label}
                      </label>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
                <div className="text-end mt-2">
                  {data.delivery?.contactName ? (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#191919"}
                        size="1x"
                      />
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#19191900"}
                        size="1x"
                      />
                    </>
                  )}
                </div>
              </div>
              <div
                className={` d-flex flex-column justify-content-between ${
                  data.delivery
                    ? " box-container hover-card-btn cursor-pointer "
                    : " box-container-noshadow "
                } `}
                onClick={() => {
                  if (id && data.delivery) {
                    openModal(
                      "delivery",
                      [id],
                      "md",
                      "",
                      dictionary.registries.receiver,
                    );
                  }
                }}
              >
                <div
                  className={`${data.delivery && data.delivery.location?.label ? "" : "text-disabled"}`}
                >
                  <h3 className={`${data.delivery ? "cursor-pointer" : ""}`}>
                    {dictionary.registries.receiver}
                  </h3>
                  {data.delivery?.contactName &&
                  data.delivery.location?.label ? (
                    <>
                      <label
                        className={`label-1 mb-1 ${
                          data.delivery ? "cursor-pointer" : ""
                        }`}
                      >
                        {deliveryName}
                      </label>
                      <label
                        className={`label-6 ${
                          data.delivery ? "cursor-pointer" : ""
                        }`}
                      >
                        {data.delivery?.location?.label}
                      </label>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
                <div className="text-end mt-2">
                  {data.delivery ? (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#191919"}
                        size="1x"
                      />
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faLongArrowRight}
                        color={"#19191900"}
                        size="1x"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* -------------------------------------------------------- */}
            {/*              CONTAINER GENERAL INFORMATION               */}
            {/* -------------------------------------------------------- */}

            <div
              style={styles.cells.generalInformation}
              className="box-container-noshadow "
            >
              <h3 data-grid-area="label">
                {dictionary.words.general_informations}
              </h3>
              <div className="d-grid gap-2" style={styles.generalInformation}>
                {/* Internal Reference */}
                <div style={styles.cells.internalReference}>
                  <label className="label-2">
                    {dictionary.references.internal_reference}
                  </label>
                  <InputBoxIcon
                    name="internalReference"
                    value={data?.internalReference || "-"}
                    type="text"
                    disabled
                  />
                </div>

                {/* External Reference */}
                <div style={styles.cells.externalReference}>
                  <label className="label-2">
                    {dictionary.references.external_reference}
                  </label>
                  <InputBoxIcon
                    name="externalReference"
                    value={data?.externalReference || "-"}
                    type="text"
                    disabled
                  />
                </div>

                {/* Creation User */}
                <div style={styles.cells.creationUser}>
                  <label className="label-2">
                    {dictionary.users.creation_user}
                  </label>
                  <InputBoxIcon
                    name="creationUser"
                    value={data?.creationUser || "-"}
                    type="text"
                    icon={faUser}
                    disabled
                  />
                </div>

                {/* Creation Date */}
                <div style={styles.cells.creationDate}>
                  <label className="label-2">
                    {dictionary.dates.creation_date}
                  </label>
                  <InputBoxIcon
                    name="creationDate"
                    value={
                      moment(data.creationDate).format("DD/MM/YYYY") || "-"
                    }
                    type="text"
                    icon={faCalendarPlus}
                    disabled
                  />
                </div>

                {/* Note */}
                <div style={styles.cells.note}>
                  <label className="label-2">{dictionary.words.note}</label>
                  <InputBoxIcon
                    name="note"
                    value={data?.note || "-"}
                    type="text"
                    disabled
                  />
                </div>

                {/* Content */}
                <div style={styles.cells.content}>
                  <label className="label-2">{dictionary.words.content}</label>
                  <InputBoxIcon
                    name="content"
                    value={data?.content || "-"}
                    type="text"
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* -------------------------------------------------------- */}
            {/*               CONTAINER LABELS AND DOCUMENTS             */}
            {/* -------------------------------------------------------- */}

            <div
              style={styles.cells.labelAndDocuments}
              className="box-container-noshadow d-flex flex-column "
            >
              <div className="d-flex justify-content-between mb-3">
                <h3 className="m-0">
                  {dictionary.words.labels} {dictionary.words.and}{" "}
                  {dictionary.words.documents}
                </h3>
                {data.orderId && (
                  <ButtonUtility>
                    <Link
                      className="text-black"
                      to={"/shipments/ddt/" + id + "/print"}
                      target="blank"
                    >
                      {dictionary.actions.print} DDT{" "}
                      <i className="ph-download ms-2"></i>
                    </Link>
                  </ButtonUtility>
                )}
              </div>
              <div className="d-flex w-100 h-100 align-items-center justify-content-center">
                {/* Carusel */}
                <AttachmentIndexProvider id={id}>
                  <AttachmentIndex />
                </AttachmentIndexProvider>
              </div>
            </div>

            {/* -------------------------------------------------------- */}
            {/*                   CONTAINER CUSTOM DATA                  */}
            {/* -------------------------------------------------------- */}
            {data.customTotalQuantity && data.customTotalQuantity > 0 && (
              <div
                style={styles.cells.customData}
                className="box-container-noshadow"
              >
                <h3>{dictionary.customs_data.customs_data}</h3>
                <CustomDataIndex />
              </div>
            )}

            {/* -------------------------------------------------------- */}
            {/*                     CONTAINER PARCEL                     */}
            {/* -------------------------------------------------------- */}

            {data.parcels && data.parcels > 0 && (
              <div
                style={styles.cells.parcels}
                className="box-container-noshadow"
              >
                <div className="d-flex align-items-center gap-3 mb-2">
                  <h3 className="m-0">{dictionary.parcels.packages_2}</h3>
                  <label
                    className="label-6 rounded-5 "
                    style={{
                      padding: "0.2rem 0.8rem",
                      backgroundColor: "#e9e9e9",
                    }}
                  >
                    {icons.parcel?.label}
                  </label>
                </div>
                <ParcelsIndex />
              </div>
            )}
          </div>

          {/* -------------------------------------------------------- */}
          {/*                   FOOTER WITH BUTTON                     */}
          {/* -------------------------------------------------------- */}
          <div className="row mb-5 mt-3 my-md-3  align-items-center">
            {/* Back button*/}
            <div className="col-12 col-md-3 mb-2 mb-md-0 text-start">
              <button
                title={dictionary.words.back}
                className="button-1 button-1-transparent w-100 w-md-auto"
                onClick={() => navigate(`/shipments`)}
              >
                <FontAwesomeIcon icon={faAngleLeft} /> {dictionary.words.back}
              </button>
            </div>

            {/* Actions */}
            <div className="col-12 col-md-9 d-flex flex-column flex-md-row flex-wrap justify-content-md-end gap-2">
              <button
                className="button-1 button-1-transparent w-100 w-md-auto"
                onClick={() => navigate(`/shipments/clone/${id}`)}
              >
                <FontAwesomeIcon icon={faClone} />{" "}
                {dictionary.actions.duplicate}
              </button>

              {data.status?.code === "DRAFT" && (
                <button
                  className="button-1 button-1-transparent w-100 w-md-auto"
                  onClick={() => navigate(`/shipments/edit/${id}`)}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />{" "}
                  {dictionary.actions.edit}
                </button>
              )}

              {data.status?.code === "DRAFT" && (
                <ButtonConfirm
                  className="w-100 w-md-auto"
                  onClick={() => navigate(`/shipments/rate/${id}`)}
                  icon={false}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />{" "}
                  {dictionary.rating.choose_rate}
                </ButtonConfirm>
              )}

              {/* Email Notifications */}
              {data?.statusType === "TRACKING" && (
                <button
                  className="button-1 button-1-transparent w-100 w-md-auto"
                  onClick={() => {
                    data.internalReference &&
                      openModal(
                        "emailNotifications",
                        [data.internalReference],
                        "lg",
                        "",
                        dictionary.notifications.email_notifications,
                      );
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />{" "}
                  {dictionary.notifications.email_notifications}
                </button>
              )}

              {/* Stock Release */}
              {data.stockReleaseEvent && !data.stockReleaseCreated && (
                <button
                  className="button-1 button-1-transparent border-warning text-white bg-warning w-100 w-md-auto "
                  onClick={() =>
                    openModal(
                      "createStockRelease",
                      [],
                      "md",
                      "",
                      dictionary.shipments.stock_release,
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={faLockOpenLight}
                    className="text-white"
                    style={{
                      width: "14px",
                      transform: "scaley(1.126)", // <--- questo controlla effettivamente le dimensioni
                    }}
                  />
                  {dictionary.shipments.stock_release}
                </button>
              )}

              {data.status?.code === "DELIVERED" &&
                data.contractService?.contract.shipmentType === "EXPRESS" && (
                  <Link
                    target="_blank"
                    to={`/shipments/return/${data.id}`}
                    className="button-1 button-1-transparent"
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} />{" "}
                    {dictionary.contracts.express_return_ms}
                  </Link>
                )}
              {data.orderId && (
                <Link
                  className="button-1 button-1-dark "
                  to={`/orders/details/${data.orderId}`}
                  title={dictionary.orders.order}
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faBagsShopping} />
                  {dictionary.actions.details} {dictionary.orders.order}
                </Link>
              )}
              {/* Book Collection */}
              {(data.status?.code === "BOOKED" ||
                data.status?.code === "CONFIRMED") &&
                !data.collectionAssigned &&
                data.contractService?.carrierService.carrier
                  .collectionCreationEnabled && (
                  <ButtonConfirm
                    className="w-100 w-md-auto"
                    onClick={() => {
                      data.id &&
                        openModal(
                          "createCollection",
                          [data.id],
                          "md",
                          "",
                          dictionary.collections
                            .do_you_want_to_book_a_collection,
                        );
                    }}
                    icon={false}
                  >
                    <FontAwesomeIcon icon={faCalendar} />{" "}
                    {dictionary.collections.do_you_want_to_book_a_collection}
                  </ButtonConfirm>
                )}
              {/* Delete */}
              {(data.status?.code === "BOOKED" ||
                data.status?.code === "DRAFT") && (
                <ButtonDelete
                  title={dictionary.actions.delete}
                  className="w-100 w-md-auto"
                  onClick={() => {
                    data.id &&
                      openModal(
                        "delete",
                        [data.id],
                        "md",
                        "",
                        dictionary.actions.delete,
                      );
                  }}
                />
              )}
              {data.status?.code === "BOOKED" && (
                <ButtonConfirm
                  className="w-100 w-md-auto "
                  onClick={() => {
                    data.id &&
                      openModal(
                        "confirm",
                        [data.id],
                        "md",
                        "",
                        dictionary.actions.confirm,
                      );
                  }}
                >
                  {dictionary.actions.confirm}
                </ButtonConfirm>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Details;
